import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { image1, image2, image3, image4 } from "./imagesStrings";
import { IOutstandingReportRow } from "../../../typings/interfaces/responses/customer-report-data-response";
import { NotesSideBox } from "../../../util/report-notes-utils";
import { getStoreHeader } from "../../../util/store-header";
import { Colors } from "../../../configs/colors";

const styles = StyleSheet.create({
  page: { fontSize: 12, fontFamily: "Helvetica" },
  infoRow: { flexDirection: "row", marginBottom: 8 },
  infoLabel: { width: "35%", fontSize: 8 },
  infoColon: { width: "5%", fontSize: 8, textAlign: "center" },
  infoValue: { width: "60%", fontSize: 8, paddingLeft: 8 },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#A1CEF5",
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginTop: 16,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: "#D0D0D0",
  },
  totalRow: {
    flexDirection: "row",
    backgroundColor: "#A1CEF5",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  colNo: { width: "12%", fontSize: 8 },
  colInvoice: { width: "30%", fontSize: 8 },
  colDate: { width: "30%", fontSize: 8 },
  colAmount: { width: "28%", fontSize: 8, textAlign: "right" },
  footer: {
    position: "absolute",
    bottom: 300,
    left: 50,
    width: "100%",
    textAlign: "center",
    fontSize: 40,
    opacity: 0.1,
    transform: "rotate(-45deg)",
  },
});

interface OutstandingReportProps {
  name: string;
  customerId: string;
  address: string;
  contactNumber: string;
  lastPaymentAmount: number;
  lastPaymentDate: string;
  totalDueAmount: number;
  outstandingRows: IOutstandingReportRow[];
  reportTitle?: string;
  period: string;
  notes?: string;
}

const OutstandingReport = ({
  name,
  customerId,
  address,
  contactNumber,
  lastPaymentAmount,
  lastPaymentDate,
  totalDueAmount,
  outstandingRows,
  reportTitle,
  period,
  notes,
}: OutstandingReportProps) => {
  const storeHeader = getStoreHeader();
  const titleParts = (reportTitle ?? "").split(" — ");
  const typeLabel = titleParts[0] || "Outstanding Report";
  const periodLabel = titleParts[1] || period;

  const rows = outstandingRows && outstandingRows.length > 0 ? outstandingRows : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image src={image1} style={{ width: "100%", height: "auto", marginBottom: 10 }} />

        <View
          style={{
            marginBottom: 15,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            position: "absolute",
            left: "8rem",
            top: "3.8rem",
            gap: "15.1rem",
          }}
        >
          <View>
            <Text style={{ fontSize: 11, fontWeight: "bold", color: "#00796B", marginBottom: 0.5 }}>
              {storeHeader.name}
            </Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{storeHeader.subtitle}</Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>{storeHeader.address}</Text>
            <Text style={{ fontSize: 9, marginBottom: 1 }}>TP: {storeHeader.phone} | {storeHeader.email}</Text>
            <Text style={{ fontSize: 9 }}>{storeHeader.website}</Text>
          </View>
          <Image src={image2} style={{ width: "100px", height: "50px" }} />
        </View>

        <View
          style={{
            borderBottomWidth: 1.5,
            borderBottomColor: Colors.PRIMARY,
            marginVertical: 10,
            marginTop: 40,
            marginLeft: 96,
            marginRight: 30,
          }}
        />

        <View
          style={{
            width: "470px",
            backgroundColor: "#F6F6F6",
            borderRadius: "15px",
            marginLeft: 96,
            padding: 18,
          }}
        >
          {/* Title row */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <Text style={{ fontSize: 14, color: "#505050", textDecoration: "underline", fontWeight: "bold" }}>
              {typeLabel}
            </Text>
            <Text style={{ fontSize: 10, color: "#505050", fontWeight: "bold" }}>
              {periodLabel}
            </Text>
          </View>

          {/* Customer info + Notes side by side (notes only as tall as info block) */}
          <View style={{ flexDirection: "row", marginBottom: 4 }}>
            <View style={{ flex: notes ? 1.6 : 1 }}>
              {[
                ["Customer Name", name],
                ["Customer ID", customerId],
                ["Address", address],
                ["Contact Number", contactNumber],
                ["Last Payment Amount", `$ ${lastPaymentAmount.toFixed(2)}`],
                ["Last Payment Date", lastPaymentDate],
                ["Total Due Amount", `$ ${totalDueAmount.toFixed(2)}`],
              ].map(([label, value], index) => (
                <View key={index} style={styles.infoRow}>
                  <Text style={styles.infoLabel}>{label}</Text>
                  <Text style={styles.infoColon}>:</Text>
                  <Text style={styles.infoValue}>{value}</Text>
                </View>
              ))}
            </View>
            <NotesSideBox notes={notes} />
          </View>

          {/* Table */}
          <View style={styles.tableHeader}>
            <Text style={styles.colNo}>No.</Text>
            <Text style={styles.colInvoice}>Invoice Number</Text>
            <Text style={styles.colDate}>Invoice Date</Text>
            <Text style={styles.colAmount}>Amount</Text>
          </View>

          {rows.length > 0 ? (
            rows.map((row, idx) => (
              <View key={idx} style={styles.tableRow}>
                <Text style={styles.colNo}>{row.no}</Text>
                <Text style={styles.colInvoice}>{row.invoiceNumber}</Text>
                <Text style={styles.colDate}>{row.invoiceDate}</Text>
                <Text style={styles.colAmount}>{row.amount.toFixed(2)}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={{ fontSize: 8, color: "#888" }}>No outstanding invoices in this period.</Text>
            </View>
          )}

          <View style={styles.totalRow}>
            <Text style={[styles.colNo, { fontWeight: "bold" }]}>Total</Text>
            <Text style={styles.colInvoice} />
            <Text style={styles.colDate} />
            <Text style={[styles.colAmount, { fontWeight: "bold" }]}>
              {totalDueAmount.toFixed(2)}
            </Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Thank You!</Text>
          <Text>See you soon...</Text>
        </View>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginLeft: 96,
            marginTop: 10,
            marginBottom: 10,
            marginRight: 30,
          }}
        >
          <Text style={{ fontSize: 6, color: "#515151" }}>Originally developed by: DryCleanX</Text>
          <Text style={{ fontSize: 6, color: "#515151" }}>
            Printed by: CBH3674 | John Roy | 01/26/2025 20:55
          </Text>
          <Image src={image3} style={{ width: 35, height: 10 }} />
        </View>

        <Image
          src={image4}
          style={{ width: "100%", height: "auto", position: "absolute", bottom: 0 }}
        />
      </Page>
    </Document>
  );
};

export default OutstandingReport;
